import { Images, ImageUp, Upload, X } from 'lucide-react'
import { type ChangeEvent, type DragEvent, useRef, useState } from 'react'
import { toast } from 'sonner'
import * as m from '~/paraglide/messages'
import { SUPPORTED_IMAGE_MIME_TYPES } from '~/shared/lib/asset-image'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import { AssetLibraryPicker } from './asset-library-picker'

function acceptsFile(accept: string, file: File): boolean {
  const patterns = accept
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
  if (patterns.length === 0) return true
  return patterns.some((pattern) =>
    pattern.endsWith('/*') ? file.type.startsWith(pattern.slice(0, -1)) : file.type === pattern,
  )
}

interface ImageUploadFieldProps {
  file: File | null
  preview: string | null
  onSelect: (file: File | null) => void
  accept?: string
  showLibrary?: boolean
  hint?: string
  subHint?: string
  compact?: boolean
  disabled?: boolean
  className?: string
}

export function ImageUploadField({
  file,
  preview,
  onSelect,
  accept = SUPPORTED_IMAGE_MIME_TYPES.join(','),
  showLibrary = true,
  hint,
  subHint,
  compact = false,
  disabled = false,
  className,
}: ImageUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  function handleFiles(files: FileList | null) {
    const picked = files?.[0]
    if (!picked) return
    if (!acceptsFile(accept, picked)) {
      toast.error(m.imageUploadInvalidType())
      return
    }
    onSelect(picked)
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    handleFiles(e.target.files)
    e.target.value = ''
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setIsDragging(false)
    if (!disabled) handleFiles(e.dataTransfer.files)
  }

  return (
    <div className={cn('space-y-2', className)}>
      {preview ? (
        <div className="group relative overflow-hidden rounded-lg border bg-muted">
          <img
            src={preview}
            alt=""
            className={cn('mx-auto w-full object-contain', compact ? 'max-h-32' : 'max-h-56')}
          />
          <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity duration-200 focus-within:opacity-100 group-hover:opacity-100">
            <Button
              asChild
              type="button"
              size="icon"
              variant="secondary"
              disabled={disabled}
              className="h-8 w-8 cursor-pointer"
            >
              <label aria-label={m.editReplace()}>
                <ImageUp className="h-4 w-4" />
                <input
                  type="file"
                  accept={accept}
                  disabled={disabled}
                  className="hidden"
                  onChange={handleInputChange}
                />
              </label>
            </Button>
            <Button
              type="button"
              size="icon"
              variant="secondary"
              disabled={disabled}
              aria-label={m.imageUploadRemove()}
              className="h-8 w-8"
              onClick={() => onSelect(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          {file && (
            <p className="truncate border-t bg-background/80 px-3 py-1.5 text-xs text-muted-foreground">
              {file.name}
            </p>
          )}
        </div>
      ) : (
        // biome-ignore lint/a11y/useSemanticElements: div needed for drag-and-drop event support
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
          onDragOver={(e) => {
            e.preventDefault()
            if (!disabled) setIsDragging(true)
          }}
          onDragLeave={(e) => {
            e.preventDefault()
            setIsDragging(false)
          }}
          onDrop={handleDrop}
          onClick={() => !disabled && inputRef.current?.click()}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
              e.preventDefault()
              inputRef.current?.click()
            }
          }}
          className={cn(
            'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed text-center transition-colors',
            compact ? 'p-5' : 'p-8',
            isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25',
            disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-primary/50',
          )}
        >
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            disabled={disabled}
            className="hidden"
            onChange={handleInputChange}
          />
          <Upload className={cn('text-muted-foreground', compact ? 'h-7 w-7' : 'h-10 w-10')} />
          <div>
            <p className="text-sm font-medium">{hint ?? m.imageUploadHint()}</p>
            <p className="text-xs text-muted-foreground">{subHint ?? m.imageUploadFormats()}</p>
          </div>
        </div>
      )}

      {showLibrary && (
        <div className="flex justify-center">
          <AssetLibraryPicker
            onSelect={(picked) => onSelect(picked)}
            trigger={
              <Button type="button" variant="outline" size="sm" disabled={disabled}>
                <Images className="h-4 w-4" />
                {m.chooseFromLibrary()}
              </Button>
            }
          />
        </div>
      )}
    </div>
  )
}
